import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField
} from '@mui/material';
import { fetchFoldersforYear } from '../../requests/Requests';

async function fetchFoldersByYear(year) {
    return await fetchFoldersforYear(year);
}

export default function CampaignFolderDropdown(props) {
    const {
        year,
        virtualFolder,
        onChange,
        disabled
    } = props;

    const [folders, setFolders] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasLoadedFolders, setHasLoadedFolders] = useState(false);
    const [error, setError] = useState('');

    const validYear = useMemo(() => {
        const yearAsString = String(year || '');
        if (!/^\d{4}$/.test(yearAsString)) return null;
        const parsedYear = Number(yearAsString);
        return parsedYear > 2000 && parsedYear < 2100 ? parsedYear : null;
    }, [year]);

    const getHelperText = () => {
        if (!validYear) return 'År kræves før du kan vælge mappe.';
        if (loading) return ' ';
        if (error) return error;
        return ' ';
    };

    const getNoOptionsText = () => {
        if (!validYear) return 'Angiv et gyldigt år først.';
        if (loading) return 'Henter mapper...';
        if (error) return error;
        if (hasLoadedFolders && folders.length === 0) return 'Ingen mapper fundet. Skriv en ny mappe.';
        return 'Ingen matchende mapper. Den indtastede mappe bruges ved gem.';
    };

    const selectedValue = virtualFolder || null;

    useEffect(() => {
        if (!validYear) {
            setFolders([]);
            setInputValue('');
            setError('');
            setHasLoadedFolders(false);
            if (virtualFolder) onChange(null);
            return;
        }

        let cancelled = false;

        async function loadFolders() {
            setLoading(true);
            setError('');
            setHasLoadedFolders(false);

            try {
                const result = await fetchFoldersByYear(validYear);
                if (!cancelled) {
                    const mapped = Array.isArray(result)
                        ? result.map((f) => f.name)
                        : [];
                    setFolders(mapped);
                    setHasLoadedFolders(true);
                }
            } catch (e) {
                if (!cancelled) {
                    setFolders([]);
                    setHasLoadedFolders(true);
                    setError('Mapper kunne ikke hentes.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadFolders();
        return () => { cancelled = true; };
    }, [validYear]);

    useEffect(() => {
        setInputValue(virtualFolder ?? '');
    }, [virtualFolder]);

    const handleFolderValue = (folderPath) => {
        const trimmed = folderPath.trim();
        if (!trimmed) {
            onChange(null);
            return;
        }
        const existing = folders.find(
            (f) => f.toLowerCase() === trimmed.toLowerCase()
        );
        onChange(existing ?? trimmed);
    };

    return (
        <Box>
            <Autocomplete
                freeSolo
                disabled={disabled || !validYear}
                readOnly={!validYear}
                loading={loading}
                options={folders}              
                value={selectedValue}         
                inputValue={inputValue}
                getOptionLabel={(option) => option ?? ''}
                isOptionEqualToValue={(option, val) => option === val}
                noOptionsText={getNoOptionsText()}
                forcePopupIcon={!!validYear && folders.length > 0}
                openOnFocus={!!validYear}
                onChange={(event, selected) => {
                    setError('');
                    if (!selected) {
                        onChange(null);
                        return;
                    }
                    // freeSolo can return a string directly
                    handleFolderValue(typeof selected === 'string' ? selected : '');
                }}
                onInputChange={(event, newInputValue, reason) => {
                    if (!validYear) return;
                    setInputValue(newInputValue);
                    setError('');
                    if (reason === 'input') handleFolderValue(newInputValue);
                    if (reason === 'clear') onChange(null);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Mappe"
                        error={!!error}
                        helperText={getHelperText()}
                        placeholder={validYear ? 'Vælg eller skriv mappe' : 'År kræves først'}
                        InputProps={{
                            ...params.InputProps,
                            readOnly: !validYear,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />
                )}
            />
        </Box>
    );
}